// App topbar — mirrors the local frontend's components/Topbar.tsx design:
// logo pair + small caption on the left; chip / theme toggle / sign-out
// (rounded-lg icon buttons) on the right; a mobile hamburger opening the
// same w-72 dropdown menu. Cloud differences: the caption shows the plan
// instead of FW/SN, and the plan badge stands in for the status pills.
import { useEffect, useState, type ComponentType } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Menu, X } from "lucide-react";
import { Moon, Sun } from "./icons";
import { useAuth } from "../auth/AuthContext";
import { DeviceNavMenu, deviceSiteIdFromPath } from "../device/DeviceNavMenu";
import { getTheme, toggleTheme } from "../lib/theme";
import { planName } from "../lib/plan";
import { PlanBadge } from "./PlanBadge";

export interface AppNavItem {
  to: string;
  end?: boolean;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  label: string;
}

export function AppTopbar({ nav }: { nav: AppNavItem[] }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [theme, setThemeState] = useState(getTheme());
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex items-center gap-3 px-4 md:px-6 h-14">
      <div className="flex items-center gap-2">
        <img src="/logo-color.png" alt={t("common.appName")} className="h-9 w-auto md:hidden" />
        <img
          src="/logo-color-full.png"
          alt={t("common.appName")}
          className="hidden md:block h-9 w-auto"
        />
        <div className="hidden sm:block">
          <div className="text-[11px] text-muted leading-none">
            {t("common.cloud")}
            {user ? ` · ${planName(user.plan)}` : ""}
          </div>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {user && (
          <span className="chip hidden sm:inline-flex" title={user.email}>
            {user.name || user.email}
          </span>
        )}
        {user && <PlanBadge plan={user.plan} />}
        <button
          className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-muted hover:text-text border border-transparent hover:border-border hover:bg-panel2 transition"
          onClick={() => {
            toggleTheme();
            setThemeState(getTheme());
          }}
          aria-label={t("nav.toggleTheme")}
        >
          {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        {user && (
          <button
            className="hidden md:inline-flex items-center justify-center h-9 w-9 rounded-lg text-muted hover:text-text border border-transparent hover:border-border hover:bg-panel2 transition"
            onClick={() => void logout()}
            title={t("nav.signOut")}
          >
            <LogOut size={16} />
          </button>
        )}
        <button
          className="btn md:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={t("nav.menu")}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {menuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-14 z-30 bg-black/40"
            onClick={() => setMenuOpen(false)}
          />
          <div className="md:hidden absolute right-2 top-[calc(100%+0.25rem)] z-40 w-72 max-h-[calc(100vh-4.5rem)] overflow-y-auto rounded-lg border border-border bg-panel shadow-xl p-2 flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
            {/* Inside a site's Manage area the phone menu carries the same
                two-level device menu as the desktop sidebar. */}
            {deviceSiteIdFromPath(location.pathname) != null && (
              <DeviceNavMenu
                siteId={deviceSiteIdFromPath(location.pathname)!}
                pathname={location.pathname}
              />
            )}
            <div className="border-t border-border" />
            {user && (
              <button
                className="nav-item text-left"
                onClick={() => {
                  setMenuOpen(false);
                  void logout();
                }}
              >
                <LogOut size={18} />
                <span>{t("nav.signOut")}</span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
